import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaPlus, FaUsers, FaPen, FaCheck, FaTimes, FaClock,
  FaExclamationTriangle, FaCalendarAlt, FaEye, FaEdit,
  FaPaperPlane, FaChartLine, FaUserGraduate, FaNewspaper,
  FaCommentDots, FaTrash, FaSave, FaLink, FaLightbulb,
  FaExternalLinkAlt, FaGlobe
} from 'react-icons/fa';
import api from '../utils/api';
import { getUser } from '../utils/auth';

const ProfessorArticlesPage = () => {
  const [assignments, setAssignments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewAssignment, setShowNewAssignment] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [journalUrls, setJournalUrls] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [assignmentsRes, classesRes] = await Promise.all([
        api.get('/articles/assignments'),
        api.get('/articles/classes')
      ]);
      setAssignments(assignmentsRes.data.assignments);
      setClasses(classesRes.data.classes);
      
      // Générer les URLs de journaux pour les classes
      const user = getUser();
      const schoolSlug = user?.schoolId || 'dailyglobe-school';
      const currentYear = new Date().getFullYear();
      const urls = classesRes.data.classes.map(cls => ({
        classId: cls.id,
        className: cls.name,
        shortName: cls.shortName || cls.name.toLowerCase().replace(/\s+/g, '-'),
        url: `/journal/${schoolSlug}/${(cls.shortName || cls.name).toLowerCase().replace(/\s+/g, '-')}/${currentYear}`
      }));
      setJournalUrls(urls);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (rate) => {
    if (rate >= 90) return 'bg-green-500';
    if (rate >= 70) return 'bg-yellow-500';
    if (rate >= 50) return 'bg-orange-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Gestion des articles</h1>
          <p className="text-gray-600">Commandez, suivez et corrigez les travaux de vos élèves</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Accès rapide au journal */}
          {journalUrls.length > 0 && (
            <div className="relative group">
              <Link
                to={journalUrls[0]?.url}
                className="px-4 py-2 bg-amber-100 text-amber-800 border-2 border-amber-300 rounded-lg font-bold hover:bg-amber-200 flex items-center gap-2"
                data-testid="btn-view-journal"
              >
                <FaNewspaper /> Voir le Journal
                {journalUrls.length > 1 && <span className="text-xs">▼</span>}
              </Link>
              {journalUrls.length > 1 && (
                <div className="absolute right-0 mt-1 bg-white border-2 border-amber-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[200px]">
                  <div className="p-2 text-xs text-gray-500 border-b font-bold uppercase tracking-wider">
                    Journaux par classe
                  </div>
                  {journalUrls.map(j => (
                    <Link
                      key={j.classId}
                      to={j.url}
                      className="block px-4 py-2 hover:bg-amber-50 text-gray-700 text-sm"
                    >
                      <FaGlobe className="inline mr-2 text-amber-600" />
                      {j.className}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
          <button
            onClick={() => setShowNewAssignment(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 flex items-center gap-2"
          >
            <FaPlus /> Nouvelle commande
          </button>
        </div>
      </div>

      {/* Bandeau accès journal */}
      {journalUrls.length > 0 && (
        <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-200 rounded-lg flex items-center justify-center">
                <FaNewspaper className="text-amber-700 text-2xl" />
              </div>
              <div>
                <h3 className="font-bold text-amber-900">Journal Public de l'École</h3>
                <p className="text-amber-700 text-sm">Consultez les articles publiés par vos élèves</p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              {journalUrls.map(j => (
                <Link
                  key={j.classId}
                  to={j.url}
                  target="_blank"
                  className="px-3 py-1.5 bg-white border-2 border-amber-300 text-amber-800 rounded-lg text-sm font-bold hover:bg-amber-100 flex items-center gap-1.5 transition-colors"
                >
                  {j.shortName?.toUpperCase() || j.className}
                  <FaExternalLinkAlt className="text-xs" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg p-4 border shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <FaPen className="text-blue-600 text-xl" />
            </div>
            <div>
              <p className="text-2xl font-bold">{assignments.length}</p>
              <p className="text-gray-600 text-sm">Commandes</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <FaClock className="text-yellow-600 text-xl" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {assignments.reduce((acc, a) => acc + (a.stats?.total - a.stats?.submitted || 0), 0)}
              </p>
              <p className="text-gray-600 text-sm">En attente</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <FaEdit className="text-orange-600 text-xl" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {assignments.reduce((acc, a) => acc + (a.stats?.submitted - a.stats?.validated || 0), 0)}
              </p>
              <p className="text-gray-600 text-sm">À corriger</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <FaNewspaper className="text-green-600 text-xl" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {assignments.reduce((acc, a) => acc + (a.stats?.validated || 0), 0)}
              </p>
              <p className="text-gray-600 text-sm">Publiés</p>
            </div>
          </div>
        </div>
      </div>

      {/* Assignments List */}
      {assignments.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-xl">
          <FaPen className="text-6xl text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-600">Aucune commande d'article</h2>
          <p className="text-gray-500 mt-2">Créez votre première commande pour vos élèves</p>
          <button
            onClick={() => setShowNewAssignment(true)}
            className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700"
          >
            <FaPlus className="inline mr-2" /> Créer une commande
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {assignments.map((assignment) => (
            <motion.div
              key={assignment.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg border shadow-sm overflow-hidden"
            >
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded font-bold">
                        {assignment.className}
                      </span>
                      <span className="text-sm text-gray-500">
                        <FaCalendarAlt className="inline mr-1" />
                        Deadline: {new Date(assignment.deadline).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-bold text-gray-800">{assignment.title}</h3>
                    
                    {assignment.description && (
                      <p className="text-gray-600 text-sm mt-1 line-clamp-2">{assignment.description}</p>
                    )}
                  </div>
                  
                  <div className="text-right">
                    <div className="flex items-center gap-4">
                      {/* Progress circle */}
                      <div className="relative w-16 h-16">
                        <svg className="w-16 h-16 transform -rotate-90">
                          <circle
                            cx="32"
                            cy="32"
                            r="28"
                            stroke="#e5e7eb"
                            strokeWidth="4"
                            fill="none"
                          />
                          <circle
                            cx="32"
                            cy="32"
                            r="28"
                            stroke="#3b82f6"
                            strokeWidth="4"
                            fill="none"
                            strokeDasharray={`${(assignment.stats?.submissionRate || 0) * 1.76} 176`}
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-sm font-bold">{assignment.stats?.submissionRate || 0}%</span>
                        </div>
                      </div>
                      
                      <div className="text-left">
                        <p className="text-sm">
                          <span className="font-bold">{assignment.stats?.submitted || 0}</span>/{assignment.stats?.total || 0} rendus
                        </p>
                        <p className="text-sm text-gray-500">
                          <span className={assignment.stats?.onTimeRate >= 70 ? 'text-green-600' : 'text-orange-600'}>
                            {assignment.stats?.onTimeRate || 0}%
                          </span> à l'heure
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Quick stats bar */}
                <div className="mt-4 flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1 text-yellow-600">
                    <FaClock /> {(assignment.stats?.total || 0) - (assignment.stats?.submitted || 0)} en attente
                  </span>
                  <span className="flex items-center gap-1 text-orange-600">
                    <FaEdit /> {(assignment.stats?.submitted || 0) - (assignment.stats?.validated || 0)} à corriger
                  </span>
                  <span className="flex items-center gap-1 text-green-600">
                    <FaCheck /> {assignment.stats?.validated || 0} validés
                  </span>
                </div>
              </div>
              
              {/* Actions */}
              <div className="bg-gray-50 px-5 py-3 flex items-center gap-3 border-t">
                <button
                  onClick={() => setSelectedAssignment(assignment)}
                  className="px-4 py-2 bg-blue-600 text-white rounded font-bold hover:bg-blue-700 flex items-center gap-2 text-sm"
                >
                  <FaEye /> Voir les soumissions
                </button>
                <button className="px-4 py-2 border border-gray-300 rounded font-bold hover:bg-gray-100 flex items-center gap-2 text-sm">
                  <FaPaperPlane /> Relancer
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {showNewAssignment && (
          <NewAssignmentModal
            classes={classes}
            onClose={() => setShowNewAssignment(false)}
            onCreated={() => {
              loadData();
              setShowNewAssignment(false);
            }}
          />
        )}
        
        {selectedAssignment && (
          <SubmissionsModal
            assignment={selectedAssignment}
            onClose={() => setSelectedAssignment(null)}
            onSelectSubmission={(sub) => {
              setSelectedSubmission(sub);
              setSelectedAssignment(null);
            }}
          />
        )}
        
        {selectedSubmission && (
          <CorrectionModal
            submission={selectedSubmission}
            onClose={() => setSelectedSubmission(null)}
            onUpdated={() => {
              loadData();
              setSelectedSubmission(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Modal nouvelle commande
const NewAssignmentModal = ({ classes, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    classId: '',
    title: '',
    description: '',
    suggestedSources: [''],
    suggestedAngles: [''],
    wordCountMin: 400,
    wordCountMax: 800,
    requirePhoto: false,
    deadline: ''
  });
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.classId || !formData.title || !formData.deadline) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setCreating(true);
    try {
      await api.post('/articles/assignments', {
        ...formData,
        suggestedSources: formData.suggestedSources.filter(s => s.trim()),
        suggestedAngles: formData.suggestedAngles.filter(a => a.trim())
      });
      onCreated();
    } catch (error) {
      console.error('Error creating assignment:', error);
      alert('Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const addSource = () => setFormData(prev => ({ ...prev, suggestedSources: [...prev.suggestedSources, ''] }));
  const addAngle = () => setFormData(prev => ({ ...prev, suggestedAngles: [...prev.suggestedAngles, ''] }));

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">Nouvelle commande d'article</h2>
          <button onClick={onClose}><FaTimes /></button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Classe */}
          <div>
            <label className="block text-sm font-bold mb-1">Classe *</label>
            <select
              value={formData.classId}
              onChange={(e) => setFormData(prev => ({ ...prev, classId: e.target.value }))}
              className="w-full p-2 border rounded-lg"
              required
            >
              <option value="">Sélectionner une classe</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>{cls.name} ({cls.studentCount} élèves)</option>
              ))}
            </select>
          </div>
          
          {/* Sujet */}
          <div>
            <label className="block text-sm font-bold mb-1">Sujet de l'article *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Ex: Interview d'un professionnel des médias"
              className="w-full p-2 border rounded-lg"
              required
            />
          </div>
          
          {/* Description */}
          <div>
            <label className="block text-sm font-bold mb-1">Instructions détaillées</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Consignes, attentes, format..."
              rows={3}
              className="w-full p-2 border rounded-lg"
            />
          </div>
          
          {/* Sources suggérées */}
          <div>
            <label className="block text-sm font-bold mb-1">
              <FaLink className="inline mr-1" /> Sources suggérées
            </label>
            {formData.suggestedSources.map((source, i) => (
              <input
                key={i}
                type="text"
                value={source}
                onChange={(e) => {
                  const newSources = [...formData.suggestedSources];
                  newSources[i] = e.target.value;
                  setFormData(prev => ({ ...prev, suggestedSources: newSources }));
                }}
                placeholder="URL ou nom de source"
                className="w-full p-2 border rounded-lg mb-2"
              />
            ))}
            <button type="button" onClick={addSource} className="text-blue-600 text-sm">+ Ajouter une source</button>
          </div>
          
          {/* Angles suggérés */}
          <div>
            <label className="block text-sm font-bold mb-1">
              <FaLightbulb className="inline mr-1" /> Angles suggérés
            </label>
            {formData.suggestedAngles.map((angle, i) => (
              <input
                key={i}
                type="text"
                value={angle}
                onChange={(e) => {
                  const newAngles = [...formData.suggestedAngles];
                  newAngles[i] = e.target.value;
                  setFormData(prev => ({ ...prev, suggestedAngles: newAngles }));
                }}
                placeholder="Angle ou point de vue"
                className="w-full p-2 border rounded-lg mb-2"
              />
            ))}
            <button type="button" onClick={addAngle} className="text-blue-600 text-sm">+ Ajouter un angle</button>
          </div>
          
          {/* Word count */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold mb-1">Nombre de mots min</label>
              <input
                type="number"
                value={formData.wordCountMin}
                onChange={(e) => setFormData(prev => ({ ...prev, wordCountMin: parseInt(e.target.value) }))}
                className="w-full p-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1">Nombre de mots max</label>
              <input
                type="number"
                value={formData.wordCountMax}
                onChange={(e) => setFormData(prev => ({ ...prev, wordCountMax: parseInt(e.target.value) }))}
                className="w-full p-2 border rounded-lg"
              />
            </div>
          </div>
          
          {/* Photo required */}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.requirePhoto}
              onChange={(e) => setFormData(prev => ({ ...prev, requirePhoto: e.target.checked }))}
              className="w-5 h-5"
            />
            <span>Photo obligatoire</span>
          </label>
          
          {/* Deadline */}
          <div>
            <label className="block text-sm font-bold mb-1">Deadline *</label>
            <input
              type="datetime-local"
              value={formData.deadline}
              onChange={(e) => setFormData(prev => ({ ...prev, deadline: e.target.value }))}
              className="w-full p-2 border rounded-lg"
              required
            />
          </div>
          
          {/* Submit */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border rounded-lg font-bold"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={creating}
              className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50"
            >
              {creating ? 'Création...' : 'Créer la commande'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Modal soumissions
const SubmissionsModal = ({ assignment, onClose, onSelectSubmission }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSubmissions();
  }, [assignment.id]);

  const loadSubmissions = async () => {
    try {
      const response = await api.get(`/articles/assignments/${assignment.id}/submissions`);
      setSubmissions(response.data.submissions);
    } catch (error) {
      console.error('Error loading submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (sub) => {
    const styles = {
      assigned: 'bg-gray-100 text-gray-800',
      draft: 'bg-blue-100 text-blue-800',
      submitted: 'bg-yellow-100 text-yellow-800',
      under_review: 'bg-orange-100 text-orange-800',
      revision_needed: 'bg-red-100 text-red-800',
      validated: 'bg-green-100 text-green-800',
      published: 'bg-purple-100 text-purple-800'
    };
    const labels = {
      assigned: 'Non rendu',
      draft: 'Brouillon',
      submitted: 'À corriger',
      under_review: 'En correction',
      revision_needed: 'À revoir',
      validated: 'Validé',
      published: 'Publié'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-bold ${styles[sub.status] || styles.assigned}`}>
        {labels[sub.status] || sub.status}
      </span>
    );
  };

  const getPunctualityBadge = (sub) => {
    if (!sub.punctuality) return null;
    return (
      <span 
        className="px-2 py-1 rounded-full text-xs font-bold text-white"
        style={{ backgroundColor: sub.punctuality.color }}
      >
        {sub.punctuality.rate}% à l'heure
      </span>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white p-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">{assignment.title}</h2>
            <p className="text-blue-200 text-sm">{assignment.className} • {submissions.length} élèves</p>
          </div>
          <button onClick={onClose}><FaTimes className="text-xl" /></button>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left p-4">Élève</th>
                  <th className="text-center p-4">Statut</th>
                  <th className="text-center p-4">Ponctualité</th>
                  <th className="text-center p-4">Mots</th>
                  <th className="text-center p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((sub) => (
                  <tr key={sub.id} className="border-t hover:bg-gray-50">
                    <td className="p-4">
                      <p className="font-bold">{sub.studentName}</p>
                      <p className="text-sm text-gray-500">{sub.studentEmail}</p>
                    </td>
                    <td className="p-4 text-center">
                      {getStatusBadge(sub)}
                    </td>
                    <td className="p-4 text-center">
                      {getPunctualityBadge(sub)}
                    </td>
                    <td className="p-4 text-center">
                      {sub.wordCount > 0 ? `${sub.wordCount} mots` : '-'}
                    </td>
                    <td className="p-4 text-center">
                      {['submitted', 'under_review', 'validated', 'published'].includes(sub.status) && (
                        <button
                          onClick={() => onSelectSubmission(sub)}
                          className="px-3 py-1 bg-blue-600 text-white rounded text-sm font-bold hover:bg-blue-700"
                        >
                          {sub.status === 'submitted' ? 'Corriger' : 'Voir'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Modal correction avancé avec les 3 décisions
const CorrectionModal = ({ submission, onClose, onUpdated }) => {
  const [globalComment, setGlobalComment] = useState('');
  const [corrections, setCorrections] = useState(submission.corrections || []);
  const [marginComments, setMarginComments] = useState(submission.professorComments || []);
  const [newCorrection, setNewCorrection] = useState({ original: '', corrected: '', comment: '' });
  const [newMarginComment, setNewMarginComment] = useState('');
  const [selectedText, setSelectedText] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('read'); // 'read', 'correct', 'comments'
  const [showConfirmModal, setShowConfirmModal] = useState(null); // 'refused', 'corrections_needed', 'approved'

  // Appliquer les corrections au texte pour affichage
  const getAnnotatedContent = () => {
    let content = submission.content || '';
    
    // Trier les corrections par position (reverse pour éviter les décalages)
    const sortedCorrections = [...corrections].sort((a, b) => 
      content.indexOf(b.original) - content.indexOf(a.original)
    );
    
    sortedCorrections.forEach(corr => {
      if (corr.original && content.includes(corr.original)) {
        const replacement = `<del class="bg-red-100 line-through text-red-600">${corr.original}</del><ins class="bg-green-100 text-green-700 font-bold">${corr.corrected}</ins>`;
        content = content.replace(corr.original, replacement);
      }
    });
    
    return content;
  };

  // Ajouter une correction
  const addCorrection = async () => {
    if (!newCorrection.original.trim()) return;
    
    try {
      const response = await api.post('/articles/submissions/add-correction', {
        submissionId: submission.id,
        original: newCorrection.original,
        corrected: newCorrection.corrected,
        comment: newCorrection.comment
      });
      
      setCorrections([...corrections, response.data.correction]);
      setNewCorrection({ original: '', corrected: '', comment: '' });
      setSelectedText('');
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  // Ajouter un commentaire en marge
  const addMarginComment = async () => {
    if (!newMarginComment.trim()) return;
    
    try {
      await api.post('/articles/submissions/add-comment', {
        submissionId: submission.id,
        field: 'content',
        comment: newMarginComment
      });
      
      setMarginComments([...marginComments, { 
        field: 'content', 
        comment: newMarginComment,
        timestamp: new Date().toISOString()
      }]);
      setNewMarginComment('');
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  // Gérer la sélection de texte
  const handleTextSelection = () => {
    const selection = window.getSelection();
    if (selection.toString().trim()) {
      setSelectedText(selection.toString());
      setNewCorrection({ ...newCorrection, original: selection.toString() });
      setActiveTab('correct');
    }
  };

  // Prendre la décision finale
  const handleDecision = async (decision) => {
    setSaving(true);
    try {
      await api.post('/articles/submissions/validate', {
        submissionId: submission.id,
        decision,
        globalComment
      });
      setShowConfirmModal(null);
      onUpdated();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la validation');
    } finally {
      setSaving(false);
    }
  };

  // Modal de confirmation
  const ConfirmDecisionModal = ({ decision, onConfirm, onCancel }) => {
    const config = {
      refused: {
        icon: <FaTimes className="text-4xl text-red-600" />,
        title: 'Refuser l\'article ?',
        description: 'L\'élève devra reprendre entièrement son article. Il recevra un email de notification.',
        confirmText: 'Oui, refuser',
        confirmClass: 'bg-red-600 hover:bg-red-700'
      },
      corrections_needed: {
        icon: <FaEdit className="text-4xl text-orange-500" />,
        title: 'Demander des corrections ?',
        description: `L'élève devra intégrer ${corrections.length} correction(s) dans son article. Il recevra un email détaillé.`,
        confirmText: 'Oui, demander corrections',
        confirmClass: 'bg-orange-500 hover:bg-orange-600'
      },
      approved: {
        icon: <FaCheck className="text-4xl text-green-600" />,
        title: 'Valider pour publication ?',
        description: 'L\'article sera marqué comme "Bon à publier". L\'élève sera notifié par email.',
        confirmText: 'Oui, valider',
        confirmClass: 'bg-green-600 hover:bg-green-700'
      }
    };
    
    const c = config[decision];
    
    return (
      <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-xl p-6 max-w-md w-full text-center"
        >
          <div className="mb-4">{c.icon}</div>
          <h3 className="text-xl font-bold mb-2">{c.title}</h3>
          <p className="text-gray-600 mb-6">{c.description}</p>
          
          <div className="flex gap-3 justify-center">
            <button
              onClick={onCancel}
              className="px-6 py-2 border-2 border-gray-300 rounded-lg font-bold hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              onClick={onConfirm}
              disabled={saving}
              className={`px-6 py-2 text-white rounded-lg font-bold ${c.confirmClass}`}
            >
              {saving ? 'Envoi...' : c.confirmText}
            </button>
          </div>
        </motion.div>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-white rounded-xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white p-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold font-serif">CORRECTION : {submission.headline || 'Article sans titre'}</h2>
            <p className="text-blue-200 text-sm">Par {submission.studentName} • {submission.wordCount} mots • Soumis {submission.timing === 'on_time' ? '✅ à l\'heure' : '⚠️ en retard'}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded"><FaTimes className="text-xl" /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b bg-gray-50">
          <button
            onClick={() => setActiveTab('read')}
            className={`px-6 py-3 font-bold text-sm ${activeTab === 'read' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
          >
            <FaEye className="inline mr-2" /> Lecture
          </button>
          <button
            onClick={() => setActiveTab('correct')}
            className={`px-6 py-3 font-bold text-sm ${activeTab === 'correct' ? 'border-b-2 border-orange-500 text-orange-600' : 'text-gray-500'}`}
          >
            <FaEdit className="inline mr-2" /> Corrections ({corrections.length})
          </button>
          <button
            onClick={() => setActiveTab('comments')}
            className={`px-6 py-3 font-bold text-sm ${activeTab === 'comments' ? 'border-b-2 border-purple-500 text-purple-600' : 'text-gray-500'}`}
          >
            <FaCommentDots className="inline mr-2" /> Commentaires ({marginComments.length})
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          <div className="flex">
            {/* Main content */}
            <div className="flex-1 p-6">
              {activeTab === 'read' && (
                <>
                  <div className="mb-6">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Titre</span>
                    <h1 className="text-2xl font-bold font-serif">{submission.headline}</h1>
                  </div>
                  
                  {submission.subheadline && (
                    <div className="mb-4">
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Sous-titre</span>
                      <p className="text-lg italic text-gray-600">{submission.subheadline}</p>
                    </div>
                  )}
                  
                  <div className="mb-6">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Chapô</span>
                    <p className="bg-blue-50 p-4 rounded-lg text-blue-900 font-medium border-l-4 border-blue-500">{submission.lead}</p>
                  </div>
                  
                  <div className="mb-6">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Article</span>
                    <div 
                      className="bg-amber-50 p-6 rounded-lg whitespace-pre-wrap font-serif text-lg leading-relaxed border border-amber-200"
                      onMouseUp={handleTextSelection}
                      dangerouslySetInnerHTML={{ __html: getAnnotatedContent() }}
                    />
                    <p className="text-xs text-gray-500 mt-2 italic">
                      💡 Sélectionnez du texte pour ajouter une correction
                    </p>
                  </div>
                  
                  {submission.photoCaption && (
                    <div className="mb-4">
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Légende photo</span>
                      <p className="italic">{submission.photoCaption}</p>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'correct' && (
                <div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                    <h3 className="font-bold text-orange-800 mb-3">✏️ Ajouter une correction</h3>
                    
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="text-xs font-bold text-gray-600 block mb-1">Texte original</label>
                        <textarea
                          value={newCorrection.original}
                          onChange={(e) => setNewCorrection({...newCorrection, original: e.target.value})}
                          placeholder="Le texte à corriger..."
                          rows={2}
                          className="w-full p-2 border rounded text-sm bg-red-50"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-gray-600 block mb-1">Texte corrigé</label>
                        <textarea
                          value={newCorrection.corrected}
                          onChange={(e) => setNewCorrection({...newCorrection, corrected: e.target.value})}
                          placeholder="Le texte corrigé..."
                          rows={2}
                          className="w-full p-2 border rounded text-sm bg-green-50"
                        />
                      </div>
                    </div>
                    
                    <input
                      type="text"
                      value={newCorrection.comment}
                      onChange={(e) => setNewCorrection({...newCorrection, comment: e.target.value})}
                      placeholder="Explication de la correction (optionnel)..."
                      className="w-full p-2 border rounded text-sm mb-3"
                    />
                    
                    <button
                      onClick={addCorrection}
                      disabled={!newCorrection.original.trim()}
                      className="px-4 py-2 bg-orange-500 text-white rounded font-bold text-sm hover:bg-orange-600 disabled:opacity-50"
                    >
                      Ajouter la correction
                    </button>
                  </div>
                  
                  {/* Liste des corrections */}
                  <h3 className="font-bold mb-3">📝 Corrections ajoutées ({corrections.length})</h3>
                  {corrections.length === 0 ? (
                    <p className="text-gray-500 italic">Aucune correction pour le moment</p>
                  ) : (
                    <div className="space-y-2">
                      {corrections.map((corr, idx) => (
                        <div key={idx} className="bg-gray-50 border rounded-lg p-3 flex items-start gap-3">
                          <span className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">{idx + 1}</span>
                          <div className="flex-1">
                            <p>
                              <del className="text-red-600 bg-red-50 px-1">{corr.original}</del>
                              {' → '}
                              <ins className="text-green-700 bg-green-50 px-1 font-bold no-underline">{corr.corrected}</ins>
                            </p>
                            {corr.comment && <p className="text-sm text-gray-500 mt-1 italic">{corr.comment}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'comments' && (
                <div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                    <h3 className="font-bold text-purple-800 mb-3">💬 Ajouter un commentaire en marge</h3>
                    <textarea
                      value={newMarginComment}
                      onChange={(e) => setNewMarginComment(e.target.value)}
                      placeholder="Votre remarque générale sur l'article..."
                      rows={3}
                      className="w-full p-2 border rounded text-sm mb-3"
                    />
                    <button
                      onClick={addMarginComment}
                      disabled={!newMarginComment.trim()}
                      className="px-4 py-2 bg-purple-500 text-white rounded font-bold text-sm hover:bg-purple-600 disabled:opacity-50"
                    >
                      Ajouter le commentaire
                    </button>
                  </div>
                  
                  <h3 className="font-bold mb-3">📋 Commentaires en marge ({marginComments.length})</h3>
                  {marginComments.length === 0 ? (
                    <p className="text-gray-500 italic">Aucun commentaire pour le moment</p>
                  ) : (
                    <div className="space-y-2">
                      {marginComments.map((c, idx) => (
                        <div key={idx} className="bg-purple-50 border-l-4 border-purple-500 p-3 rounded-r">
                          <p className="text-purple-900">{c.comment}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Footer avec commentaire global et actions */}
        <div className="border-t bg-gray-50 p-4">
          <div className="mb-4">
            <label className="text-xs font-bold text-gray-600 block mb-1">Commentaire global pour l'élève</label>
            <textarea
              value={globalComment}
              onChange={(e) => setGlobalComment(e.target.value)}
              placeholder="Votre appréciation générale sur le travail..."
              rows={2}
              className="w-full p-3 border rounded-lg text-sm"
            />
          </div>
          
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {corrections.length > 0 && <span className="mr-4">📝 {corrections.length} correction(s)</span>}
              {marginComments.length > 0 && <span>💬 {marginComments.length} commentaire(s)</span>}
            </div>
            
            <div className="flex gap-3">
              {/* Bouton REFUSER */}
              <button
                onClick={() => setShowConfirmModal('refused')}
                className="px-5 py-2 border-2 border-red-500 text-red-600 rounded-lg font-bold hover:bg-red-50 flex items-center gap-2"
                data-testid="btn-refuse-article"
              >
                <FaTimes /> Refuser
              </button>
              
              {/* Bouton CORRECTIONS */}
              <button
                onClick={() => setShowConfirmModal('corrections_needed')}
                disabled={corrections.length === 0}
                className="px-5 py-2 border-2 border-orange-500 text-orange-600 rounded-lg font-bold hover:bg-orange-50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="btn-corrections-needed"
              >
                <FaEdit /> Corrections à intégrer
              </button>
              
              {/* Bouton BON À PUBLIER */}
              <button
                onClick={() => setShowConfirmModal('approved')}
                className="px-5 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 flex items-center gap-2"
                data-testid="btn-approve-article"
              >
                <FaCheck /> Bon à publier
              </button>
            </div>
          </div>
        </div>
      </motion.div>
      
      {/* Modal de confirmation */}
      {showConfirmModal && (
        <ConfirmDecisionModal
          decision={showConfirmModal}
          onConfirm={() => handleDecision(showConfirmModal)}
          onCancel={() => setShowConfirmModal(null)}
        />
      )}
    </motion.div>
  );
};

export default ProfessorArticlesPage;
